// ---------------------------------------------------------------------------
// Scoring.
//
// Two rules here are easy to get wrong and expensive to get wrong:
//
//   1. A team scores BEST-OF, not the sum. Its round score is its highest
//      scoring member's score for that round. A solo player is a one-member
//      team, so their team score is simply their own.
//   2. A host override wins over the dictionary in both directions. 'allow'
//      forces a word valid, 'reject' forces it invalid.
//
// Everything is pure. Nothing here reads a clock.
// ---------------------------------------------------------------------------
import { AGGREGATE_NAMES_SHOWN, AGGREGATE_ROWS, AUDIENCE_SEAT_ID, CONUNDRUM_POINTS, LETTERS_NINE_BONUS_MULTIPLIER, LETTERS_TILE_COUNT, NUMBERS_SCORING, } from './constants.js';
import { stepsToExpression } from './numbers.js';
// --- Per-answer scoring ----------------------------------------------------
export function scoreLettersWord(word, valid) {
    if (!valid)
        return 0;
    const length = word.trim().length;
    if (length <= 0)
        return 0;
    // A nine is worth double. Capped at nine, since nine tiles cannot yield more
    // and a longer string reaching here would be a bug, not a bigger score.
    if (length >= LETTERS_TILE_COUNT)
        return LETTERS_TILE_COUNT * LETTERS_NINE_BONUS_MULTIPLIER;
    return length;
}
export function scoreNumbersValue(value, target) {
    if (value === null || !Number.isFinite(value))
        return 0;
    const distance = Math.abs(value - target);
    for (const band of NUMBERS_SCORING) {
        if (distance <= band.withinInclusive)
            return band.points;
    }
    return 0;
}
/**
 * The conundrum's ten points.
 *
 * `first` is the buzzer rule and only the buzzer rule: one player gets in, and a
 * correct answer from anybody else — which in a buzzered room cannot happen, but
 * the type does not know that — is worth nothing.
 *
 * **With `conundrumBuzzer` off there is no first**, so every caller on that path
 * passes `true` for it. That is not a fudge: the setting exists because in a room
 * of two hundred the buzzer is a latency contest one person wins, and paying only
 * the fastest is the contest with extra steps. See `ConundrumRound.buzzerOff`,
 * which is where a round records which rule it was dealt under.
 */
export function scoreConundrum(correct, first) {
    return correct && first ? CONUNDRUM_POINTS : 0;
}
// --- Round scoring ---------------------------------------------------------
/** The host has the last word on validity, in both directions. */
function effectiveValidity(submission) {
    if (submission.hostOverride === 'allow')
        return true;
    if (submission.hostOverride === 'reject')
        return false;
    return submission.valid === true;
}
/** Earliest lock first; a player who never locked sorts last. */
function compareLockedAt(a, b) {
    if (a === b)
        return 0;
    if (a === null)
        return 1;
    if (b === null)
        return -1;
    return a - b;
}
function compareIds(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
/**
 * Who gets the conundrum's ten points.
 *
 * `solvedBy` is the authoritative record when it is set, because buzz arrival
 * is RTT compensated and the raw lock timestamps are not. It is only honoured
 * when that player is still correct after any host override, so an override can
 * move the points.
 */
function firstCorrectConundrumSolver(round) {
    const correct = Object.entries(round.submissions)
        .filter(([, submission]) => submission.kind === 'conundrum' && effectiveValidity(submission))
        .map(([playerId, submission]) => ({ playerId, lockedAt: submission.lockedAt }));
    if (correct.length === 0)
        return null;
    if (round.solvedBy !== null && correct.some((entry) => entry.playerId === round.solvedBy)) {
        return round.solvedBy;
    }
    correct.sort((a, b) => compareLockedAt(a.lockedAt, b.lockedAt) || compareIds(a.playerId, b.playerId));
    return correct[0].playerId;
}
/** Whether this round was dealt in a room with the conundrum buzzer switched off. */
export function conundrumBuzzerOff(round) {
    return round.type === 'conundrum' && round.buzzerOff === true;
}
function scoreSubmission(round, playerId, submission, conundrumSolver) {
    const valid = effectiveValidity(submission);
    switch (submission.kind) {
        case 'letters':
            return scoreLettersWord(submission.word, valid);
        case 'numbers': {
            if (round.type !== 'numbers' || round.target === null)
                return 0;
            return scoreNumbersValue(valid ? submission.value : null, round.target);
        }
        case 'conundrum':
            // With no buzzer there is nobody to be first: everybody who has it right
            // when the clock stops scores. `conundrumSolver` is null on that path and
            // is not consulted, which is what keeps the two rules from half-mixing.
            return scoreConundrum(valid, conundrumBuzzerOff(round) || playerId === conundrumSolver);
    }
}
/** Score every submission in a judged round. Returns playerId -> points. */
export function scoreRound(round) {
    const conundrumSolver = round.type === 'conundrum' && !conundrumBuzzerOff(round)
        ? firstCorrectConundrumSolver(round)
        : null;
    const scores = {};
    for (const [playerId, submission] of Object.entries(round.submissions)) {
        scores[playerId] = scoreSubmission(round, playerId, submission, conundrumSolver);
    }
    return scores;
}
// --- Team scoring ----------------------------------------------------------
/**
 * The score bucket a player contributes to: their team, or themselves when they
 * have no team. This is the single place the "solo players are one-member
 * teams" rule lives.
 */
function scoreKeyOf(teamId, playerId) {
    return teamId ?? playerId;
}
/**
 * Whether this player's answers count towards the standing of the game running
 * now.
 *
 * Not `role === 'player'`, and the difference is `leave-seat`. Somebody who
 * stood down mid-game is a spectator — they answer nothing and are never the
 * chooser — but the rounds they already played still happened, so dropping them
 * out of the standing would quietly delete points a room watched them score.
 * They simply stop gaining any.
 *
 * One implementation, read by every total in this file, so the round score, the
 * standing, the winner and the placement cannot disagree about who is playing.
 */
export function seatScoresThisGame(player) {
    return player.role === 'player' || (player.leftSeatAt ?? null) !== null;
}
/**
 * Team totals. A team scores best-of: its highest-scoring member's answer for
 * that round, not the sum. Solo players count as one-member teams.
 *
 * `audienceSeat` adds the folded audience answer as a bucket of its own. It is
 * never on a team — there is nothing to put it on one — so it is always its own
 * best-of, and it is included whether or not it can win: `audienceCanWin` is
 * about the winner, not about the score, and a seat shown in the standing with
 * no number beside it would be worse than either.
 */
export function teamScoresForRound(round, players, options) {
    const perPlayer = scoreRound(round);
    const totals = {};
    for (const [playerId, player] of Object.entries(players)) {
        if (!seatScoresThisGame(player))
            continue;
        const key = scoreKeyOf(player.teamId, playerId);
        const score = perPlayer[playerId] ?? 0;
        const best = totals[key];
        totals[key] = best === undefined ? score : Math.max(best, score);
    }
    if (options?.audienceSeat === true) {
        totals[AUDIENCE_SEAT_ID] = perPlayer[AUDIENCE_SEAT_ID] ?? 0;
    }
    return totals;
}
/**
 * Whether the audience is a competitor in this room right now.
 *
 * **The setting being on is not enough, and reading it as though it were was
 * the bug.** `audienceSeat` is a rule about how a round is scored, and it was
 * read directly as "there is a player called The audience" — so switching it on
 * in an empty lobby put a fake person in the roster, among real names, with a
 * score of nothing, before a single answer existed anywhere in the room. It is
 * not a person, and a roster is a list of people.
 *
 * Three conditions, each ruling out a different way of drawing a competitor
 * with nobody behind it:
 *
 * **Not in the lobby.** There are no rounds yet, so there is nothing it could
 * have answered. The lobby talks about the audience where the SETTING is, which
 * is where a host is deciding it — not in the list of who is here.
 *
 * **Somebody has to actually be out there.** A room with the setting on and an
 * empty bench folds nothing into nothing. `audienceSubmission` already returns
 * null with no candidates, so this is that same fact asked one step earlier,
 * before a name reaches a screen.
 *
 * **Or it has already answered.** This is the case a live spectator count gets
 * wrong on its own: an audience that played six rounds and then went home has a
 * score the standing must keep showing, or a competitor vanishes mid-game and
 * everybody below it moves up a place for no reason a viewer can see.
 *
 * Shared with `toClientState` on purpose. The roster it draws and the buckets
 * scored here are the same claim about whether this seat exists, and two copies
 * of it would eventually disagree about a game in progress.
 */
export function audienceSeatAppears(state) {
    if (state.settings.audienceSeat !== true)
        return false;
    if (state.phase === 'lobby')
        return false;
    if (Object.values(state.players).some((player) => player.role === 'spectator'))
        return true;
    return state.rounds.some((round) => round.submissions[AUDIENCE_SEAT_ID] !== undefined);
}
export function cumulativeTeamScores(state) {
    const totals = {};
    const audienceSeat = audienceSeatAppears(state);
    // Seed every bucket so a game with no rounds still reports zeroes. An EMPTY
    // team gets no bucket, deliberately: best-of over no members is not zero, it
    // is nothing, and a row for a team nobody is on would sit in the standing
    // tying for last with a score it cannot have earned.
    for (const [playerId, player] of Object.entries(state.players)) {
        if (!seatScoresThisGame(player))
            continue;
        totals[scoreKeyOf(player.teamId, playerId)] = 0;
    }
    if (audienceSeat)
        totals[AUDIENCE_SEAT_ID] = 0;
    for (const round of state.rounds) {
        const roundTotals = teamScoresForRound(round, state.players, { audienceSeat });
        for (const [key, score] of Object.entries(roundTotals)) {
            totals[key] = (totals[key] ?? 0) + score;
        }
    }
    return totals;
}
// --- Reveal ----------------------------------------------------------------
/** A submission with nothing in it. Shown as a dash, revealed first. */
function submittedNothing(submission) {
    if (submission.declaredLength === null)
        return true;
    switch (submission.kind) {
        case 'letters':
            return submission.word.trim().length === 0;
        case 'numbers':
            return submission.value === null;
        case 'conundrum':
            return submission.answer.trim().length === 0;
    }
}
/**
 * Reveal order. Players who submitted nothing come first as a dash, then the
 * rest worst-first, so the best answer in the round lands last. Invalid words
 * appear at their claimed length. Ties break on lock time, earliest first.
 *
 * "Worst" is a different quantity in each discipline, and reading it off
 * `declaredLength` alone was wrong for one of them.
 *
 * **Letters and the conundrum** rank on `declaredLength` ascending, which is
 * the length of the word: shortest first, longest last.
 *
 * **Numbers** ranks on distance from the target, furthest first. For a numbers
 * submission `declaredLength` is not a length at all — `lockNumbers` stores the
 * declared VALUE in it — so sorting it ascending ordered the round by how big
 * everybody's arithmetic came out. Against a target of 105 that put the player
 * who declared 998 in the last slot, the one the whole reveal is built to pay
 * off, and the player who actually hit 105 near the front with nothing after
 * them. Distance is what the round is scored on, so it is what the round is
 * revealed on.
 */
export function revealOrder(round) {
    const target = round.type === 'numbers' ? round.target : null;
    const entries = Object.entries(round.submissions).map(([playerId, submission]) => ({
        playerId,
        nothing: submittedNothing(submission),
        rank: revealRank(submission, target),
        lockedAt: submission.lockedAt,
    }));
    entries.sort((a, b) => Number(b.nothing) - Number(a.nothing) ||
        a.rank - b.rank ||
        compareLockedAt(a.lockedAt, b.lockedAt) ||
        compareIds(a.playerId, b.playerId));
    return entries.map((entry) => entry.playerId);
}
/**
 * The sort key `revealOrder` puts in ascending order: smaller comes out first.
 *
 * A numbers answer's key is the negated distance from the target, so an answer
 * miles away sorts before one that is close and an exact hit sorts last of all
 * at zero. Negating rather than reversing the comparison keeps every other rule
 * in `revealOrder` — nothing-first, then lock time, then id — pointing the same
 * way for all three round types.
 *
 * `target` is null for letters and the conundrum, and also for a numbers round
 * closed before anyone called a board. Falling back to the declared value there
 * is the old behaviour, and with no target there is nothing better to say.
 */
function revealRank(submission, target) {
    const declared = submission.declaredLength ?? 0;
    if (submission.kind !== 'numbers' || target === null)
        return declared;
    if (submission.value === null || !Number.isFinite(submission.value))
        return declared;
    return -Math.abs(submission.value - target);
}
// --- Standings -------------------------------------------------------------
/**
 * Winner ids. More than one entry is a shared win, which is a real outcome.
 *
 * Ids are in the same space as `cumulativeTeamScores`: a team id for a player
 * on a team, a player id for a solo player. Returning member ids instead would
 * make every three-person team win read as a three-way tie.
 */
export function determineWinners(state) {
    const totals = cumulativeTeamScores(state);
    // The audience is scored and shown either way; `audienceCanWin` decides only
    // whether it is eligible to take the top of the standing. A host who wants the
    // bench as colour rather than as a competitor turns it off, and the number
    // stays on the screen — hiding the score as well would make "the audience
    // cannot win" indistinguishable from "the audience is not playing".
    const keys = Object.keys(totals).filter((key) => key !== AUDIENCE_SEAT_ID || state.settings.audienceCanWin !== false);
    if (keys.length === 0)
        return [];
    let best = -Infinity;
    for (const key of keys) {
        const score = totals[key];
        if (score > best)
            best = score;
    }
    return keys.filter((key) => totals[key] === best);
}
/**
 * The same winners, resolved into something an end-game screen can render.
 *
 * `determineWinners` returns score-bucket ids, which is right for scoring and
 * wrong for display: a team id looks exactly like a player id, so a three-person
 * team's win renders as a three-way tie unless the caller knows which it holds.
 * This carries the distinction in the type instead of leaving it to a lookup.
 */
export function resolveWinners(state) {
    return determineWinners(state).map((key) => {
        const team = state.teams.find((candidate) => candidate.id === key);
        if (!team)
            return { kind: 'player', playerId: key };
        const memberIds = state.playerOrder.filter((playerId) => {
            const player = state.players[playerId];
            return player !== undefined && seatScoresThisGame(player) && player.teamId === team.id;
        });
        return { kind: 'team', teamId: team.id, memberIds };
    });
}
/** Placement per player, 1-indexed, sharing a placement on a tie. */
export function placements(state) {
    const totals = cumulativeTeamScores(state);
    const ranked = Object.entries(state.players)
        .filter(([, player]) => seatScoresThisGame(player))
        .map(([playerId, player]) => ({
        playerId,
        score: totals[scoreKeyOf(player.teamId, playerId)] ?? 0,
    }));
    // 1 plus however many are strictly ahead: ties share, and the next placement
    // skips, so two firsts are followed by a third.
    //
    // **Sorted once rather than scanned per seat, and that stopped being a detail
    // when the player cap went.** This was `ranked.filter(...)` inside the loop
    // over `ranked` — quadratic, which is nothing at the sixteen seats that used
    // to be the ceiling (256 comparisons) and is 25 million at
    // `MAX_PLAYERS_HARD_CAP`. It runs on the way to `game-over` and again for
    // every record write, so it is on the path a five-hundred-seat room takes at
    // the moment everybody is waiting for the standing.
    //
    // Descending by score, then one pass: a seat's placement is the index of the
    // first seat with its score, which only moves when the score does.
    const sorted = [...ranked].sort((a, b) => b.score - a.score);
    const out = {};
    let placeOfCurrentScore = 1;
    for (let index = 0; index < sorted.length; index += 1) {
        const entry = sorted[index];
        // Not `index + 1`: a tie has to keep the placement the run started on, or
        // two firsts would come out as first and second.
        if (index === 0 || entry.score !== sorted[index - 1].score) {
            placeOfCurrentScore = index + 1;
        }
        out[entry.playerId] = placeOfCurrentScore;
    }
    return out;
}
function audienceCandidatesOf(round) {
    const target = round.type === 'numbers' ? round.target : null;
    const out = [];
    for (const [playerId, submission] of Object.entries(round.spectatorSubmissions)) {
        if (!effectiveValidity(submission))
            continue;
        if (submittedNothing(submission))
            continue;
        let quality;
        switch (submission.kind) {
            case 'letters':
                quality = scoreLettersWord(submission.word, true);
                break;
            case 'numbers':
                if (target === null || submission.value === null)
                    continue;
                quality = -Math.abs(submission.value - target);
                break;
            case 'conundrum':
                quality = CONUNDRUM_POINTS;
                break;
        }
        out.push({ playerId, submission, quality });
    }
    // Worst first, so the percentile below is an index from the bottom. The two
    // tie-breaks are only there to make the answer deterministic: two spectators
    // with the same quality are the same answer as far as the audience is
    // concerned, and which of them is named is not a fact anybody can act on.
    out.sort((a, b) => a.quality - b.quality ||
        compareLockedAt(a.submission.lockedAt, b.submission.lockedAt) ||
        compareIds(a.playerId, b.playerId));
    return out;
}
/**
 * Which candidate the room's chosen aggregation lands on, as an index into the
 * worst-first list above.
 *
 * Nearest-rank, so every percentile names a real answer rather than an
 * interpolation between two of them. `mean` is the odd one and cannot be a rank
 * at all — it is the candidate whose quality is closest to the arithmetic mean,
 * with a tie going to the better one, because the audience overstating itself by
 * one letter is a friendlier failure than understating itself.
 */
function audienceIndex(candidates, how) {
    const count = candidates.length;
    const rank = (p) => Math.min(count - 1, Math.max(0, Math.ceil(p * count) - 1));
    switch (how) {
        case 'best':
            return count - 1;
        case 'p75':
            return rank(0.75);
        case 'median':
            return rank(0.5);
        case 'mean': {
            const mean = candidates.reduce((sum, entry) => sum + entry.quality, 0) / count;
            let best = 0;
            let bestGap = Infinity;
            candidates.forEach((entry, index) => {
                const gap = Math.abs(entry.quality - mean);
                if (gap <= bestGap) {
                    best = index;
                    bestGap = gap;
                }
            });
            return best;
        }
    }
}
/**
 * The audience's answer for one judged round, or null when the bench put up
 * nothing that counts.
 *
 * Null rather than an empty answer on purpose: an empty submission would take a
 * row in the reveal and a dash in the standing, which reads as the audience
 * having played and failed. A bench where nobody answered has not played.
 *
 * The score is left null. `judgeRound` runs `scoreRound` over the round once the
 * audience is in it, exactly as it does for every other seat, so there is one
 * scorer rather than a second one here that could disagree with it.
 */
export function audienceSubmission(round, settings) {
    if (settings.audienceSeat !== true)
        return null;
    const candidates = audienceCandidatesOf(round);
    if (candidates.length === 0)
        return null;
    // **The conundrum ignores the aggregation entirely**, and there is nothing to
    // aggregate: every correct answer is the same word, so the only question is
    // whether anybody watching got it, and the earliest one is the honest witness
    // for when. `audienceCandidatesOf` already sorted by lock time within a
    // quality, and every conundrum candidate has the same quality.
    const chosen = round.type === 'conundrum'
        ? candidates[0]
        : candidates[audienceIndex(candidates, settings.audienceAggregation ?? 'median')];
    const source = chosen.submission;
    const base = {
        playerId: AUDIENCE_SEAT_ID,
        declaredLength: source.declaredLength,
        // The real answer's own timing, never a fresh stamp. It is the moment
        // somebody in the room actually played this, which is the only true thing
        // there is to say about when the audience answered.
        lockedAt: source.lockedAt,
        firstKeystrokeAt: null,
        msIntoRound: source.msIntoRound,
        valid: true,
        score: null,
        judgedValid: true,
        // A host ruling names a seat, and the audience is not one. Whatever ruling
        // sat on the spectator answer this was folded from has already decided
        // whether it was a candidate at all — see `effectiveValidity` above.
        hostOverride: null,
        actionId: null,
        fromDraft: false,
        reasoning: null,
    };
    switch (source.kind) {
        case 'letters':
            return { ...base, kind: 'letters', word: source.word };
        case 'numbers':
            return { ...base, kind: 'numbers', steps: source.steps.map((step) => ({ ...step })), value: source.value };
        case 'conundrum':
            return { ...base, kind: 'conundrum', answer: source.answer };
    }
}
// --- The aggregated reveal --------------------------------------------------
/**
 * How one answer is written for grouping, and how it will be drawn.
 *
 * Letters and the conundrum group on the word, which is the obvious thing and
 * the right one. **Numbers group on the VALUE and not on the working**, because
 * two people who both reached 723 have played the same answer as far as this
 * screen is concerned even though almost nobody reaches it the same way — and
 * grouping on the expression would produce two hundred rows of one, which is the
 * un-aggregated reveal with extra steps. The expression drawn is the first one
 * in reveal order that reached the value, so the row still shows working
 * somebody actually did.
 */
function aggregateKeyOf(round, submission) {
    switch (submission.kind) {
        case 'letters':
            return { key: `w:${submission.word}`, answer: submission.word };
        case 'conundrum':
            return { key: `c:${submission.answer}`, answer: submission.answer };
        case 'numbers': {
            const tiles = round.type === 'numbers' ? round.tiles : [];
            const expression = submission.steps.length > 0
                ? stepsToExpression(tiles, submission.steps)
                : String(submission.value ?? '');
            return { key: `n:${submission.value ?? 'none'}`, answer: expression };
        }
    }
}
/** Ascending, contiguous, and a bucket with nothing in it is still a bucket. */
function bucketsOf(values) {
    if (values.length === 0)
        return [];
    const counts = new Map();
    let low = Infinity;
    let high = -Infinity;
    for (const value of values) {
        counts.set(value, (counts.get(value) ?? 0) + 1);
        if (value < low)
            low = value;
        if (value > high)
            high = value;
    }
    const out = [];
    for (let value = Math.min(0, low); value <= high; value += 1) {
        out.push({ value, count: counts.get(value) ?? 0 });
    }
    return out;
}
/**
 * How far from the target a numbers answer is, for the histogram.
 *
 * **Capped one past the last scoring band.** Distances run to nine hundred and a
 * histogram with nine hundred buckets is not a histogram; and past ten the game
 * itself stops distinguishing, since every band that pays anything is inside it.
 * So `AGGREGATE_FAR` is the "further than ten away" bucket and everything in it
 * scored nothing, which is exactly what a room reads off that end of the chart.
 */
const AGGREGATE_FAR = 11;
function numbersDistanceBucket(submission, target) {
    if (submission.kind !== 'numbers' || target === null || submission.value === null)
        return null;
    return Math.min(AGGREGATE_FAR, Math.abs(submission.value - target));
}
/**
 * One round drawn as a distribution rather than as a list of people.
 *
 * Null before the round is judged, which is the same gate `best` and
 * `revealOrder` sit behind and for the same reason: this is built out of the
 * judged submissions and must not reveal a thing about them one tick earlier.
 *
 * `seatIds` is every seat the round could have had an answer from, the audience
 * included. It is what `noAnswer` is counted against — a seat that put up
 * nothing has no submission at all, so counting it needs the roster rather than
 * the answers.
 *
 * **The payload does not grow with the room, and that is the whole point.** Rows
 * are capped at `AGGREGATE_ROWS` and each row names at most
 * `AGGREGATE_NAMES_SHOWN` players; everybody else is a number. A version of this
 * that carried every id would be the broadcast this screen exists to avoid,
 * rendered differently.
 */
export function buildRoundAggregate(round, seatIds) {
    if (round.revealedAt === null)
        return null;
    const target = round.type === 'numbers' ? round.target : null;
    const order = revealOrder(round);
    // Scored here rather than read off `submission.score`, which is written by
    // `judgeRound` and is null on anything built by hand. One scorer, so a row's
    // number and the standing's cannot come apart.
    const perPlayer = scoreRound(round);
    const rows = new Map();
    let noAnswer = 0;
    const answerValues = [];
    const scores = [];
    const seen = new Set();
    // Reveal order rather than object order, so the names on a row read in the
    // same order the round would have revealed them.
    for (const playerId of order) {
        const submission = round.submissions[playerId];
        if (submission === undefined)
            continue;
        seen.add(playerId);
        const score = perPlayer[playerId] ?? 0;
        scores.push(score);
        if (submittedNothing(submission)) {
            noAnswer += 1;
            continue;
        }
        if (round.type === 'numbers') {
            const bucket = numbersDistanceBucket(submission, target);
            if (bucket !== null)
                answerValues.push(bucket);
        }
        else {
            answerValues.push(submission.declaredLength ?? 0);
        }
        const { key, answer } = aggregateKeyOf(round, submission);
        const valid = effectiveValidity(submission);
        // The score is part of the key, so `AggregateEntry.score` really is what
        // every player on the row got. Two identical answers can score differently
        // on exactly one round type — a buzzered conundrum, where the points go to
        // whoever got in — and a row quietly reporting one of them for both would be
        // the screen telling somebody they scored ten when they scored nothing.
        const rowKey = `${key}|${valid}|${score}`;
        const existing = rows.get(rowKey);
        if (existing === undefined) {
            rows.set(rowKey, {
                answer,
                score,
                valid,
                playerIds: [playerId],
                count: 1,
            });
        }
        else {
            existing.count += 1;
            if (existing.playerIds.length < AGGREGATE_NAMES_SHOWN)
                existing.playerIds.push(playerId);
        }
    }
    // Seats that never put anything up at all. `seen` covers everybody with a
    // submission, empty ones included, so this is the rest of the roster.
    for (const seatId of seatIds) {
        if (seen.has(seatId))
            continue;
        noAnswer += 1;
        scores.push(0);
    }
    // **Ranked by score, never by popularity.** The word forty people found is
    // usually a short one, so a popularity ranking puts the round's best answer
    // below its most obvious one. Popularity is still on every row as `count`.
    const ranked = [...rows.values()].sort((a, b) => b.score - a.score || b.count - a.count || compareIds(a.answer, b.answer));
    const top = ranked.slice(0, AGGREGATE_ROWS).map((row) => ({
        answer: row.answer,
        score: row.score,
        valid: row.valid,
        playerIds: [...row.playerIds],
        count: row.count,
    }));
    const rest = ranked.slice(AGGREGATE_ROWS);
    return {
        top,
        otherAnswers: rest.length,
        otherPlayers: rest.reduce((sum, row) => sum + row.count, 0),
        noAnswer,
        // Only seats that actually answered are bucketed here. A seat with nothing
        // has no length and no distance, so giving it a bucket would mean either
        // inventing a zero for it — indistinguishable from a real zero-length answer
        // — or a hole in the numbers chart. `noAnswer` is the honest home for them
        // and it is right beside this.
        histogram: bucketsOf(answerValues),
        scoreHistogram: bucketsOf(scores),
    };
}
